<script setup>
import { ref, computed, watch } from 'vue'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxPopup } from 'devextreme-vue/popup'
import { DxForm } from 'devextreme-vue/form'

import { useRoute, useRouter } from 'vue-router'

import CustomerDropDownBox from '@/components/shared/CustomerDropDownBox.vue'

import { getI18n } from '@/plugins/i18n'

import {
  QStepper,
  QStep,
  QRadio,
} from 'quasar'

import { useRegistryStore } from '@/stores/registry.store'

import * as dataGrid from '@/composables/data-grid'

import { useColumns, useFormData, useFormStructure } from '@/composables/registry/subject-discount-grid'
import { registryApi } from '@/apis/registry.api'

import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import GridLayoutConfigTemplate from '@/components/shared/GridLayoutConfigTemplate.vue'

import { useEntityLock } from '@/services/entity-lock.service'

const gridId = 'subject-discount-grid'

const i18n = getI18n()
const registryStore = useRegistryStore()
const route = useRoute()
const router = useRouter()
const initialFilterSupplierBrandRef = ref(route.query.supplier_brand_ref)
const initialFilterSubjectRef = ref(route.query.subject_ref)

const { gridRef } = dataGrid.useGridRef()
const gridDataSource = dataGrid.useODataSource(registryApi.getAbsoluteUrl() + registryApi.subjectDiscountsUrl)

const formRef = ref(null)

const entityLock = useEntityLock({
  baseLockUrl: registryStore.api.getAbsoluteUrl() + registryStore.api.subjectDiscountsUrl,
  modelName: 'SubjectDiscount',
  gridRef,
})

const rowButtons = entityLock.getEntityLockButtons({
  visible: {
    edit: (e) => e.entityId < 0 && !entityLock.isLocked(false, e.entityId),
    delete: (e) => e.entityId < 0 && !entityLock.isLocked(false, e.entityId)
  }
})

const KEY_TO_FLAG = {
  supplier_brand_ref: registryStore.subjectDiscountEditableFieldMaskFlag.SUPPLIER_BRAND_REF,
  discount_category_ref: registryStore.subjectDiscountEditableFieldMaskFlag.DISCOUNT_CATEGORY_REF,
  customer_id: registryStore.subjectDiscountEditableFieldMaskFlag.CUSTOMER_ID,
  supplier_ref: registryStore.subjectDiscountEditableFieldMaskFlag.SUPPLIER_REF,
  discount_list_ref: registryStore.subjectDiscountEditableFieldMaskFlag.DISCOUNT_LIST_REF,
  priceRange: registryStore.subjectDiscountEditableFieldMaskFlag.PRICE_RANGE,
  is_urgent: registryStore.subjectDiscountEditableFieldMaskFlag.IS_URGENT,
}
const showWizard = ref(false)
const step = ref(1)
const typeChosen = ref(registryStore.subjectDiscountTypes.SELL)
const isBuy = computed(() => typeChosen.value === registryStore.subjectDiscountTypes.BUY)

const selected = ref([])
const formData = ref({})
const currentSupplierBrandRef = ref(null)
const editors = {}

watch(typeChosen, (newVal) => {
  if (newVal === registryStore.subjectDiscountTypes.BUY) {
    selected.value = selected.value.filter(k => !['customer_id', 'priceRange', 'discount_list_ref'].includes(k))
  }
})

const formItems = useFormData(selected, currentSupplierBrandRef, isBuy, editors)
const formStructure = useFormStructure(selected, isBuy)

function openWizard() {
  step.value = 1
  selected.value = []
  showWizard.value = true
}

function closeWizard() {
  showWizard.value = false
  formData.value = {}
}

function saveFromKeyboard() {
  if (showWizard.value) { return saveRecord() }

  document.activeElement.blur()
  const grid = gridRef.value.instance
  grid.getController('validating').validate(true).done(isValid => isValid && grid.saveEditData())
}

function cancelFromKeyboard() {
  if (showWizard.value) {
    closeWizard()
  } else {
    gridRef.value.instance.cancelEditData()
  }
}

function selectedToBitmask(selected) {
  return selected.reduce((mask, key) => {
    const flag = KEY_TO_FLAG[key]
    return flag ? mask | flag : mask
  }, 0)
}

function bitmaskToSelected(mask) {
  return Object.entries(KEY_TO_FLAG)
    .filter(([, flag]) => (mask & flag) !== 0)
    .map(([key]) => key)
}

async function saveRecord(stayOpen = false) {
  const validationResult = formRef.value?.instance?.validate()
  if (!validationResult || !validationResult.isValid) {
    return
  }
  const payload = { ...formData.value, type: typeChosen.value, editable_fields_mask: selectedToBitmask(selected.value) }
  await registryApi.createSubjectDiscountAsync(payload)

  formData.value = {}
  step.value = 1
  selected.value = []

  if (!stayOpen) {
    closeWizard()
  }
  gridRef.value.instance.refresh()
}

const onPopupInitialized = (e) => e.component.registerKeyHandler('escape', () => {})

const popupToolbar = computed(() => {
  const items = [
    {
      widget: 'dxButton',
      toolbar: 'bottom',
      location: 'after',
      options: {
        text: i18n.t('common.cancel'),
        onClick: closeWizard,
      },
    },
  ]

  switch (step.value) {
    case 1:
      items.unshift({
        widget: 'dxButton',
        toolbar: 'bottom',
        location: 'after',
        options: {
          text: i18n.t('common.next'),
          type: 'default',
          onClick: () => { step.value = 2 },
        },
      })
      break

    case 2:
      items.unshift(
        {
          widget: 'dxButton',
          toolbar: 'bottom',
          location: 'after',
          options: {
            text: i18n.t('common.back'),
            stylingMode: 'text',
            onClick: () => { step.value = 1 },
          },
        },
        {
          widget: 'dxButton',
          toolbar: 'bottom',
          location: 'after',
          options: {
            text: i18n.t('common.next'),
            type: 'default',
            disabled: selected.value.length === 0 || selected.value.every(k => k === 'is_urgent'),
            onClick: () => { step.value = 3 },
          },
        },
      )
      break

    case 3:
      items.unshift({
        widget: 'dxButton',
        toolbar: 'bottom',
        location: 'after',
        options: {
          text: i18n.t('common.back'),
          stylingMode: 'text',
          onClick: () => { step.value = 2 },
        },
      })
      items.push({
        widget: 'dxButton',
        toolbar: 'bottom',
        location: 'after',
        options: {
          text: i18n.t('common.saveAndNew'),
          type: 'default',
          onClick: () => saveRecord(true),
        },
      })
      items.push({
        widget: 'dxButton',
        toolbar: 'bottom',
        location: 'after',
        options: {
          text: i18n.t('common.save'),
          type: 'default',
          onClick: () => saveRecord(false),
        },
      })
      break
  }

  return items
})

const columns = useColumns(dataGrid)

const { defaultDialogTitle } = dataGrid.useDefaultDialogTitle()

const editing = dataGrid.useDefaultEditing(i => defaultDialogTitle(i, i18n.t('common.discount')),
  {
    colCount: 2,
    items: [
      {
        itemType: 'group',
        caption: i18n.t('common.mainData'),
        colCount: 3,
        items: [
          { dataField: 'type', editorOptions: { readOnly: true }},
          { itemType: 'empty', colSpan: 2 },
          { dataField: 'supplier_brand_ref', editorOptions: { showClearButton: true }},
          { dataField: 'discount_category_ref', editorOptions: { showClearButton: true }},
          { itemType: 'empty' },
          { dataField: 'customer_id', editorOptions: { showClearButton: true}},
          { dataField: 'supplier_ref', editorOptions: { showClearButton: true}},
          { dataField: 'discount_list_ref', editorOptions: { showClearButton: true }},
          { dataField: 'price_min', editorType: 'dxNumberBox', editorOptions: { showSpinButtons: true, min: 0, format: 'currency' }},
          { dataField: 'price_max', editorType: 'dxNumberBox', editorOptions: { showSpinButtons: true, min: 0, format: 'currency' }},
          { dataField: 'is_urgent'},
        ]
      },
      {
        itemType: 'group',
        caption: i18n.t('common.discountData'),
        colCount: 3,
        items: [
          { dataField: 'discount', editorType: 'dxNumberBox', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions() }},
          { dataField: 'discount_2', editorType: 'dxNumberBox', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions() }},
          { dataField: 'discount_3', editorType: 'dxNumberBox', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions() }},
          { dataField: 'charge_percentage', editorType: 'dxNumberBox', editorOptions: dataGrid.usePercentageOptions()},
          { itemType: 'empty', colSpan: 2 },
          { dataField: 'max_applicable', editorType: 'dxNumberBox', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions() }},
        ]
      },
    ]
  },
  {
    allowAdding: false,
    allowUpdating: true,
    allowDeleting: true,
    onInitializedCustom: (e) => entityLock.readOnlyToolbarItems(e),
    entityLock: entityLock
  }
)

const onToolbarPreparing = e => {
  e.toolbarOptions.items = e.toolbarOptions.items.filter(
    item => item.name !== 'addRowButton',
  )
  e.toolbarOptions.items.unshift({
    locateInMenu: 'auto',
    location: 'after',
    widget: 'dxButton',
    options: {
      icon: 'add',
      hint: `${i18n.t('common.add')} ${i18n.t('common.discount')}`,
      onClick: openWizard,
    },
  })
  e.toolbarOptions.items.unshift(dataGrid.gridLayoutConfigToolbarItem)
}

const onEditorPreparing = (e) => {
  const defaultValueChanged = e.editorOptions.onValueChanged
  if (e.parentType === 'dataRow' && e.dataField === 'supplier_brand_ref') {
    e.editorOptions.onValueChanged = (args) => {
      e.row.data.discount_category_ref = null
      currentSupplierBrandRef.value = args.value
      if (editors['discount_category_ref']) {
        if (!args.value) {
          editors['discount_category_ref'].option('dataSource', registryStore.getLocalizedDiscountCategories())
        } else {
          editors['discount_category_ref'].option('dataSource', registryStore.getLocalizedDiscountCategories().filter(c => c.supplier_brand_ref === args.value))
        }
      }
      defaultValueChanged(args)
    }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'discount_category_ref') {
    e.editorOptions.onInitialized = (e) => { editors['discount_category_ref'] = e.component }
    if (!currentSupplierBrandRef.value && !e.row?.data?.supplier_brand_ref) {
      e.editorOptions.dataSource = registryStore.getLocalizedDiscountCategories()
    } else {
      e.editorOptions.dataSource = registryStore.getLocalizedDiscountCategories().filter(c => c.supplier_brand_ref === (currentSupplierBrandRef.value ?? e.row?.data?.supplier_brand_ref))
    }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'supplier_ref') {
    e.editorOptions.onInitialized = (e) => { editors['supplier_ref'] = e.component }
    e.editorOptions.disabled = e.row?.data?.type === registryStore.subjectDiscountTypes.SELL
  }
  if (e.parentType === 'dataRow' && e.dataField === 'customer_id') {
    e.editorOptions.onInitialized = (e) => { editors['customer_id'] = e.component }
    e.editorOptions.disabled = e.row?.data?.type === registryStore.subjectDiscountTypes.BUY
  }
  if (e.parentType === 'dataRow' && e.dataField === 'type') {
    e.editorOptions.onInitialized = (e) => { editors['type'] = e.component }
    e.editorOptions.onValueChanged = (args) => {
      if (args.value === registryStore.subjectDiscountTypes.BUY) {
        typeChosen.value = registryStore.subjectDiscountTypes.BUY
        e.row.data.customer_id = null
        editors['customer_id']?.option('value', null)
        editors['customer_id']?.option('disabled', true)
        editors['supplier_ref']?.option('disabled', false)
      }
      if (args.value === registryStore.subjectDiscountTypes.SELL) {
        typeChosen.value = registryStore.subjectDiscountTypes.SELL
        e.row.data.supplier_ref = null
        editors['supplier_ref']?.option('value', null)
        editors['customer_id']?.option('disabled', false)
        editors['supplier_ref']?.option('disabled', true)
      }
      defaultValueChanged(args)
    }
  }
  if (e.parentType === 'dataRow') {
    const mask = e.row?.data?.editable_fields_mask ?? 0
    const Flags = registryStore.subjectDiscountEditableFieldMaskFlag

    const isFieldEditable = (flag) => (mask & flag) !== 0

    if (e.dataField === 'supplier_brand_ref') {
      e.editorOptions.disabled = !isFieldEditable(Flags.SUPPLIER_BRAND_REF)
    }
    if (e.dataField === 'discount_category_ref') {
      e.editorOptions.disabled = !isFieldEditable(Flags.DISCOUNT_CATEGORY_REF)
    }
    if (e.dataField === 'customer_id') {
      const editable = isFieldEditable(Flags.CUSTOMER_ID)
      e.editorOptions.disabled = !editable
    }
    if (e.dataField === 'supplier_ref') {
      const editable = isFieldEditable(Flags.SUPPLIER_REF)
      e.editorOptions.disabled = !editable
    }
    if (e.dataField === 'discount_list_ref') {
      e.editorOptions.disabled = !isFieldEditable(Flags.DISCOUNT_LIST_REF)
    }
    if (e.dataField === 'price_min' || e.dataField === 'price_max') {
      e.editorOptions.disabled = !isFieldEditable(Flags.PRICE_RANGE)
    }
    if (e.dataField === 'is_urgent') {
      e.editorOptions.disabled = !isFieldEditable(Flags.IS_URGENT)
    }
  }
}

const onEditingStart = (e) => {
  currentSupplierBrandRef.value = e.data.supplier_brand_ref
  selected.value = bitmaskToSelected(e.data.editable_fields_mask ?? 0)
}

const onContentReady = (e) => {
  dataGrid.useDefaultOnContentReady(e)
  if (gridRef.value.instance) {
    if (initialFilterSupplierBrandRef.value) {
      gridRef.value.instance.columnOption('supplier_brand_ref', 'filterValue', Number(initialFilterSupplierBrandRef.value))
    }
    if (initialFilterSubjectRef.value) {
      gridRef.value.instance.columnOption('subject_ref', 'filterValue', Number(initialFilterSubjectRef.value))
    }
  }
}

const onClearFilter = () => {
  initialFilterSupplierBrandRef.value = null
  initialFilterSubjectRef.value = null
  router.replace()
}

const onSaving = (e) => {
  if (e.changes?.[0]?.type !== 'remove') {
    for (const change of e.changes) {
      delete change.data.customer
    }
  }
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  addRowFunction: () => {
    if (!showWizard.value) openWizard()
  },
  saveFunction: saveFromKeyboard,
  saveFunctionEnabled: () => showWizard.value && step.value === 3,
  cancelFunction: cancelFromKeyboard,
  cancelFunctionEnabled: () => showWizard.value,
  columns,
  editing,
  onEditorPreparing,
  onToolbarPreparing: onToolbarPreparing,
  onEditingStart: onEditingStart,
  onContentReady: onContentReady,
  onClearFilter: onClearFilter,
  onSaving: onSaving,
})
</script>

<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #gridLayoutConfigTemplate>
        <grid-layout-config-template
          :grid-ext-ref="gridRef"
          :grid-ext-id="gridId"
        />
      </template>
      <template #customerEditor="{ data: cellInfo }">
        <customer-drop-down-box
          show-clear-button
          :data="cellInfo"
        />
      </template>
      <template
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'SubjectDiscount'"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="entityLock"
          :is-locked-available="true"
          :buttons="rowButtons"
        />
      </template>
    </dx-data-grid>

    <dx-popup
      v-model:visible="showWizard"
      :drag-enabled="true"
      :resize-enabled="true"
      :width="'80%'"
      height="auto"
      :title="`${i18n.t('common.add')} ${i18n.t('common.discount')}`"
      :show-title="true"
      :toolbar-items="popupToolbar"
      @initialized="onPopupInitialized"
    >
      <template #content>
        <q-stepper
          v-model="step"
          flat
          animated
        >
          <q-step
            :name="1"
            :title="i18n.t('common.discountType')"
            icon="mdi-percent"
            :done="step > 1"
          >
            <div class="row q-col-gutter-md">
              <q-radio
                v-model="typeChosen"
                :val="registryStore.subjectDiscountTypes.SELL"
                :label="i18n.t('common.discountSell')"
              />
              <q-radio
                v-model="typeChosen"
                :val="registryStore.subjectDiscountTypes.BUY"
                :label="i18n.t('common.discountBuy')"
              />
            </div>
          </q-step>

          <q-step
            :name="2"
            :title="i18n.t('common.chooseFields')"
            icon="mdi-format-list-bulleted"
            :done="step > 2"
          >
            <dx-form
              :items="formStructure"
              :col-count="2"
              :label-location="'left'"
            />
          </q-step>

          <q-step
            :name="3"
            :title="i18n.t('common.discountData')"
            icon="mdi-percent"
          >
            <dx-form
              ref="formRef"
              :form-data="formData"
              :items="formItems"
              :col-count="2"
              :col-count-by-screen="{ 'lg': 2, 'md': 2, 'sm': 1, 'xs': 1 }"
            >
              <template #customerEditor>
                <customer-drop-down-box
                  :key="formData.customer_id"
                  :data="{data: {customer: registryStore.customers.find(c => c.id === formData.customer_id)} || null}"
                  :value="formData.customer_id"
                  show-clear-button
                  :disabled="isBuy || !selected.includes('customer_id')"
                  @value-changed="(e) => { formData.customer_id = e?.value?.id }"
                />
              </template>
            </dx-form>
          </q-step>
        </q-stepper>
      </template>
    </dx-popup>
  </div>
</template>
